import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme'

import { Parameter, Parametercategory, ParametercategoryFactory } from '../../resource/';
import { IAuthProps, Query } from '../../services/';

import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';
import { Section } from '../../modules';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { CategoryTree } from '../../controls/CategoryTree';

interface IProps {
  /** Initial form data. */
  data: Parameter;
  /** Called whenever form changes. */
  onChange: (data: Parameter, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

interface IState {
  /** Current form data. */
  data: Parameter;
  parametercategories: Parametercategory[];
}

class ParameterForm extends React.Component<IAuthProps & IProps, IState> {
  constructor(props: IAuthProps & IProps) {
    super(props);

    // Intialize state.
    this.state = {
      data: props.data,
      parametercategories: []
    };    
  }

  componentDidMount = () => {
    // Retrieve a list of parametercategories:
    let query = new Query('name', 'asc');
    ParametercategoryFactory.getSome(this.props.auth, 0, 999, query)
      .then((res) => this.setState({ parametercategories: res.items }));    
  }

  private handleCategoryClick = (cat: Parametercategory) => {
    let idx = this.state.data.parametercategories.findIndex(mycat => mycat.id == cat.id);
    if(idx == -1) {
      this.state.data.parametercategories.push(cat);
    } else {
      this.state.data.parametercategories.splice(idx, 1);
    }
    this.setState({
      data: {
        ...this.state.data,
        parametercategories: this.state.data.parametercategories
      }
    });
  }

  renderName = () =>
    <Form.Field 
      name="name" label="Name"
      value={this.state.data.name}
      control={(<Input type="text" placeholder="Name" fluid/>)}
      required={{message: "A parameter name is required."}}
      minLength={{length: 3, message: "Parameter name must be at least 3 characters."}}
      maxLength={{length: 255, message: "Parameter name may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a parameter name, e.g. <code>Current speed</code>.</React.Fragment>)}/>     

  renderUnit = () =>
    <Form.Field 
      name="unit" label="Unit"
      value={this.state.data.unit}
      control={(<Input type="text" placeholder="Unit" fluid/>)}
      required={{message: "A parameter unit is required."}}
      maxLength={{length: 255, message: "Parameter unit may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a parameter unit, e.g. <code>m/s</code>.</React.Fragment>)}/>           

  renderMaxvalue = () =>
    <Form.Field 
      name="maxvalue" label="Maximum value"
      value={this.state.data.maxvalue}
      control={(<Input type="text" placeholder="Maximum value" fluid/>)}
      required={{message: "A parameter maximum allowable value is required."}}
      isFloat={{message: "Maximum allowable value must be numeric."}}
      hint={(<React.Fragment>Please enter a maximum allowable value.</React.Fragment>)}/>

  renderMinvalue = () =>
    <Form.Field 
      name="minvalue" label="Minimum value"
      value={this.state.data.minvalue}
      control={(<Input type="text" placeholder="Minimum value" fluid/>)}
      required={{message: "A parameter minimum allowable value is required."}}
      isFloat={{message: "Minimum allowable value must be numeric."}}
      hint={(<React.Fragment>Please enter a minimum allowable value.</React.Fragment>)}/>

  renderRecommendedHigh = () =>
    <Form.Field 
      name="recommended_high" label="Recommended high value"
      value={this.state.data.recommended_high}
      control={(<Input type="text" placeholder="Recommended high value" fluid/>)}
      required={{message: "A recommended allowable high value is required."}}
      isFloat={{message: "Recommended high value must be numeric."}}
      hint={(<React.Fragment>Please enter a recommended allowable high value.</React.Fragment>)}/>
  
  renderRecommendedLow = () =>
    <Form.Field 
      name="recommended_low" label="Recommended low value"
      value={this.state.data.recommended_low}
      control={(<Input type="text" placeholder="Recommended low value" fluid/>)}
      required={{message: "A recommended allowable low value is required."}}
      isFloat={{message: "Recommended low value must be numeric."}}
      hint={(<React.Fragment>Please enter a recommended allowable low value.</React.Fragment>)}/>

  renderDecimals = () => 
    <Form.Field 
      name="decimals" label="Decimals"
      value={this.state.data.decimals}
      control={(<Input type="text" placeholder="Number of decimals" fluid/>)}
      required={{message: "Number of decimals is required."}}
      isInt={{message: "Number of decimals must be a whole number."}}
      min={{value: 0, message: "Number of decimals must be 0 or greater."}}
      max={{value: 10, message: "Number of decimals must be 10 or less."}}
      hint={(<React.Fragment>Please enter the number of decimals to display.</React.Fragment>)}/>     

  renderType = () =>
    <Form.Field 
      name="type" label="Type"
      value={this.state.data.type}
      control={<Dropdown placeholder="Type" fluid data={["primary", "derived"]}
        label={(item:string) => item.charAt(0).toUpperCase() + item.slice(1)}>
        <Dropdown.Column>{(item:string) => item.charAt(0).toUpperCase() + item.slice(1)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select the parameter type.</React.Fragment>)}/>          

  renderFormula = () =>
    <Form.Field 
      name="formula" label="Formula"
      value={this.state.data.formula}
      control={(<Input type="text" placeholder="Formula" fluid/>)}
      maxLength={{length: 255, message: "Parameter formula may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a parameter formula.</React.Fragment>)}/>
            
  render() {
    let p = this.props;
    return (
      <Form
        data={this.state.data} dirty={p.dirty} onChange={p.onChange} onValidate={p.onValidate}>
        <Tabs underlined>
          <Tabs.Pane label="General">
            <Section padded>
              <Flex.Columns count={2}>
                <>
                  {this.renderName()}
                  {this.renderUnit()}
                  {this.renderDecimals()}
                </>
                <>
                  {this.renderType()}
                  {this.renderFormula()}
                  <Form.Uncontrolled label="Categories">
                    <TreeContainer>
                      <CategoryTree 
                        items={this.state.parametercategories} 
                        active={this.state.data.parametercategories.map(sc => sc.id)}
                        onClick={this.handleCategoryClick}/>
                    </TreeContainer>
                  </Form.Uncontrolled>                  
                </>
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
          <Tabs.Pane label="Limits">
            <Section padded>
              <Flex.Columns count={2}>
                <>
                  {this.renderMinvalue()}
                  {this.renderMaxvalue()}
                </>
                <>
                  {this.renderRecommendedLow()}
                  {this.renderRecommendedHigh()}
                </>
              </Flex.Columns>
            </Section>
          </Tabs.Pane>          
        </Tabs>
      </Form>
    )
  }
}

const TreeContainer = styled('div')`
  width: 100%;
  height: 280px;
  overflow-y: scroll;
  border: solid 1px #E0E1E2;
`

export { ParameterForm };
